import React, { useState, useEffect, useCallback } from 'react';
import { holidaysApi, Holiday, CreateHolidayData } from '../../api/holidaysApi';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import './Holidays.css';

export function Holidays() {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filter state
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [includeOptional, setIncludeOptional] = useState(true);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<CreateHolidayData>({
    name: '',
    date: '',
    day_type: 'full',
    is_optional: false,
  });

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const response = await holidaysApi.getHolidays({
        year: selectedYear,
        include_optional: includeOptional,
      });
      setHolidays(response.holidays);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, includeOptional]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleAddHoliday = () => {
    setEditingHoliday(null);
    setFormData({
      name: '',
      date: '',
      day_type: 'full',
      is_optional: false,
    });
    setShowModal(true);
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      day_type: holiday.day_type,
      is_optional: holiday.is_optional,
    });
    setShowModal(true);
  };

  const handleDeleteHoliday = async (holiday: Holiday) => {
    if (!window.confirm(`Are you sure you want to delete "${holiday.name}"?`)) {
      return;
    }

    try {
      await holidaysApi.deleteHoliday(holiday.id);
      setSuccess(`Holiday "${holiday.name}" deleted successfully`);
      fetchHolidays();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete holiday');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (editingHoliday) {
        await holidaysApi.updateHoliday(editingHoliday.id, formData);
        setSuccess(`Holiday "${formData.name}" updated successfully`);
      } else {
        await holidaysApi.createHoliday(formData);
        setSuccess(`Holiday "${formData.name}" created successfully`);
      }
      setShowModal(false);
      fetchHolidays();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save holiday');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear - 2; y <= currentYear + 2; y++) {
      years.push(y);
    }
    return years;
  };

  return (
    <div className="holidays-page">
      <header className="holidays-header">
        <div className="holidays-title">
          <h1>Holiday Management</h1>
          <p>Manage company holidays and observances</p>
        </div>
        <Button variant="primary" onClick={handleAddHoliday}>
          <Icons.Plus /> Add Holiday
        </Button>
      </header>

      {error && (
        <div className="alert alert-error">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <Icons.Check />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      <Card>
        <div className="holidays-filters">
          <div className="filter-group">
            <label>Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {getYearOptions().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>
              <input
                type="checkbox"
                checked={includeOptional}
                onChange={(e) => setIncludeOptional(e.target.checked)}
              />
              Show Optional Holidays
            </label>
          </div>
        </div>

        <div className="holidays-summary">
          <span className="summary-item">
            <strong>{holidays.length}</strong> Holidays in {selectedYear}
          </span>
          <span className="summary-item">
            <strong>{holidays.filter(h => !h.is_optional).length}</strong> Mandatory
          </span>
          <span className="summary-item">
            <strong>{holidays.filter(h => h.is_optional).length}</strong> Optional
          </span>
        </div>

        <div className="holidays-table-wrapper">
          <table className="holidays-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Type</th>
                <th>Optional</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {holidays.length === 0 ? (
                <tr>
                  <td colSpan={5} className="no-data">
                    <div className="empty-state">
                      <Icons.Calendar />
                      <p>No holidays found for {selectedYear}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                holidays.map((holiday) => (
                  <tr key={holiday.id}>
                    <td className="date-cell">{formatDate(holiday.date)}</td>
                    <td className="name-cell">{holiday.name}</td>
                    <td>
                      <span className={`badge badge-${holiday.day_type}`}>
                        {holiday.day_type === 'full' ? 'Full Day' : 'Half Day'}
                      </span>
                    </td>
                    <td>
                      {holiday.is_optional ? (
                        <span className="badge badge-optional">Optional</span>
                      ) : (
                        <span className="badge badge-mandatory">Mandatory</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <button
                        className="action-btn edit"
                        onClick={() => handleEditHoliday(holiday)}
                        title="Edit"
                      >
                        <Icons.Edit />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDeleteHoliday(holiday)}
                        title="Delete"
                      >
                        <Icons.Trash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <Icons.X />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="name">Holiday Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Christmas Day"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="date">Date *</label>
                  <input
                    type="date"
                    id="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="day_type">Day Type</label>
                  <select
                    id="day_type"
                    value={formData.day_type}
                    onChange={(e) => setFormData({ ...formData, day_type: e.target.value as 'full' | 'half' })}
                  >
                    <option value="full">Full Day</option>
                    <option value="half">Half Day</option>
                  </select>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_optional}
                      onChange={(e) => setFormData({ ...formData, is_optional: e.target.checked })}
                    />
                    <span>Optional Holiday</span>
                  </label>
                  <small>Optional holidays are not automatically applied to all employees</small>
                </div>
              </div>

              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? 'Saving...' : editingHoliday ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Holidays;
